import { FC, useState } from 'react';

import { PlusIcon } from '../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import Button from '../../../../../components/button/index';
import { EmptySearchList } from '../../../../../components/empty-search-list/index';
import { FilterRadioGroup } from '../../../../../components/filter/index';
import Loader from '../../../../../components/loader/index';
import { PaginationController } from '../../../../../components/pagination-controller/index';
import { Search } from '../../../../../components/search/index';
import { useTableSort } from '../../../../../components/table/hooks/use-table-sort.hook';
import { Table } from '../../../../../components/table/index';
import { usePagination, useScreenSize } from '../../../../../hooks/index';
import { selectedCompanyStorage } from '../../../../../storage/company/selected-company.util';
import { AccessRole } from '../../../../../types/stakeholder.types';
import {
  statusFilterMap,
  statusFilterTranslationKey,
  TemplateFilter,
} from '../../enums/template-filter.const';
import { useTemplates } from '../../hooks/use-templates.hook';
import { TemplateFormStore } from '../../store/template-form.store';
import { TemplatesEmptyState } from '../empty-state/templates-empty-state';
import { Row } from './row/template-row';
import { Column, columnOrderMap, columnTitleMap } from './template-table.types';

const ITEMS_TO_FETCH_MAP = [5, 10, 20, 50, 100];

const columnsWithSort: Column[] = [Column.NAME, Column.EDITED_ON];

const columnToSortBy: Record<Column, 'name' | 'updatedAt'> = {
  [Column.NAME]: 'name',
  [Column.EDITED_ON]: 'updatedAt',
  [Column.ACTIONS]: 'updatedAt',
  [Column.CATEGORY]: 'updatedAt',
  [Column.SIGNATORIES]: 'updatedAt',
  [Column.STATUS]: 'updatedAt',
};

export const TemplatesTable: FC = () => {
  const [currentPage, setCurrentPage] = usePagination('documents-templates');
  const [itemsToFetch, setItemsToFetch] = useState(ITEMS_TO_FETCH_MAP[1]);
  const [columnsTitle, sort] = useTableSort<Column>(columnsWithSort, columnTitleMap);
  const [filter, setFilter] = useState<TemplateFilter>(TemplateFilter.ALL);
  const [search, setSearch] = useState('');
  const { isMobile } = useScreenSize();
  const toggleAddState = TemplateFormStore(({ toggleAddState }) => toggleAddState);
  const companyId = selectedCompanyStorage(({ companyId }) => companyId);
  const company = selectedCompanyStorage(({ company }) => company);
  const hideTemplateCreation = company?.stakeholder?.accessRole === AccessRole.AUDITOR;
  const statusParam = filter === TemplateFilter.ALL ? undefined : filter;

  const take = isMobile ? 5 : itemsToFetch;

  const { count, isPending, templates, totalPages } = useTemplates({
    currentPage,
    search: search.trim() || undefined,
    sortBy: sort.field ? columnToSortBy[sort.field] : undefined,
    sortOrder: sort.order ?? undefined,
    status: statusParam,
    take,
    enabled: !!companyId,
  });

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const hasTemplates = templates.length > 0;
  const hasSearch = search.trim().length > 0;
  const showEmptySearch = !hasTemplates && hasSearch;

  return (
    <div className="flex w-full flex-col lg:pb-6">
      <div
        className={`flex flex-col rounded-md lg:shadow-[0_-1px_3px_0_rgba(16,24,40,0.04),0_2px_3px_0_rgba(16,24,40,0.1),0_1px_2px_0_rgba(16,24,40,0.06)] ${!hasTemplates || showEmptySearch ? 'min-h-[calc(100vh-140px)]' : ''} `}
      >
        <div className="flex items-center justify-between p-4">
          <FilterRadioGroup
            callback={setFilter}
            disabled={hasSearch || (!hasTemplates && filter === TemplateFilter.ALL)}
            translation={statusFilterTranslationKey}
            value={filter}
            variants={statusFilterMap}
          />

          <div className="flex gap-3">
            <Search
              callback={(value) => {
                setSearch(value);
              }}
            />
            {!hideTemplateCreation && (
              <Button
                className="hidden w-fit shrink-0 flex-nowrap gap-1 text-nowrap py-[6px] pl-4 pr-5 lg:flex"
                onClick={() => toggleAddState(true)}
              >
                <PlusIcon />
                <AppFormattedMessage id="TEMPLATE" />
              </Button>
            )}
          </div>
        </div>

        <div
          className={`max-sm:border-y max-sm:pb-4 flex flex-col rounded-md lg:border-t lg:border-gray-100 ${(isPending && !hasSearch) || !templates.length ? 'flex-1 items-center justify-center' : ''}`}
        >
          {isPending && !hasSearch ? (
            <div className="flex w-full flex-1 items-center justify-center">
              <Loader />
            </div>
          ) : !templates.length ? (
            showEmptySearch ? (
              <div className="radial-gradient-border-yellow centered relative z-1 flex h-fit w-full max-w-[393px] flex-col gap-6 rounded-2xl px-6 py-8">
                <EmptySearchList className="m-0" />
              </div>
            ) : (
              <TemplatesEmptyState />
            )
          ) : isMobile ? (
            <div className="flex h-fit w-full flex-wrap justify-center gap-4 px-4 py-4">
              {templates.map((data) => (
                <Row data={data} key={data.id} />
              ))}
            </div>
          ) : (
            <div className="flex w-full flex-col items-center gap-3">
              <div className="w-full">
                <Table
                  columnOrder={columnOrderMap}
                  columnsTitle={columnsTitle}
                  tHeadClassNames={{ thClassName: 'pl-4' }}
                >
                  {templates.map((data) => (
                    <Row data={data} key={data.id} mode="table" />
                  ))}
                </Table>
              </div>
            </div>
          )}
        </div>
        {!isPending && hasTemplates && (
          <PaginationController
            currentPage={currentPage}
            itemsCount={count}
            itemsToFetch={itemsToFetch}
            itemsToFetchMap={ITEMS_TO_FETCH_MAP}
            onClick={handlePageChange}
            onFetchAmountChange={setItemsToFetch}
            totalPages={totalPages}
            wrapperClassName="w-full px-4 max-lg:px-4 lg:border-t lg:border-gray-100"
          />
        )}
      </div>
    </div>
  );
};
